import { getEnvConfig } from '@hbs-us/contracts';
import { type ApiFetchOptions, createApiTransport, selectCsrfToken } from '@hbs-us/ui';
import { getAccessToken } from '../auth/tokenStore.js';
import { getAppEnv, getTenantSubdomain } from './env.js';

/**
 * The dashboard's binding of the ONE shared api transport (`@hbs-us/ui` `createApiTransport`).
 * The mechanism — credentialed cross-origin fetch, the `X-Tenant-Subdomain` header, the
 * double-submit CSRF token, and the single-flight refresh-and-retry on a 401 — lives there and
 * is shared with the platform-admin console. This file declares only what is TRUE OF THIS APP:
 *
 *   1. the token store it reads (`../auth/tokenStore.js`),
 *   2. the api host for the env this SPA is served from,
 *   3. `ApiError`, whose `details` field is what this app's callers read,
 *   4. the 402 rule: a payment-required refusal means the tenant's subscription is the blocker,
 *      so the whole SPA goes to the one screen that can fix it. The admin console has no such
 *      screen and deliberately does not do this.
 *
 * The api host is ALWAYS read from `@hbs-us/contracts` `getEnvConfig(env).domains.api` — never
 * hardcoded (RESEARCH anti-pattern, threat T-20-07: the SPA ships no secret and reads only the
 * public host).
 */

function getApiHost(): string {
  return getEnvConfig(getAppEnv()).domains.api;
}

export class ApiError extends Error {
  readonly status: number;
  /**
   * The parsed response body, when there was one.
   *
   * `message` carries the stable error CODE, which is what picks the translation. Some refusals
   * also carry the detail that makes them actionable — `overlaps_existing` names the shift it
   * collided with, and "that overlaps something" without saying which is not something a
   * supervisor can act on. Keeping the body here means a caller can read that detail without
   * every endpoint having to smuggle it into the code string.
   */
  readonly details: Record<string, unknown> | null;

  constructor(status: number, message: string, details: Record<string, unknown> | null = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

const transport = createApiTransport({
  getApiHost,
  getTenantSubdomain,
  getAccessToken,
  createError: (status, message, body) =>
    new ApiError(
      status,
      message,
      body && typeof body === 'object' ? (body as Record<string, unknown>) : null,
    ),
  onPaymentRequired: () => {
    if (typeof window !== 'undefined' && window.location.pathname !== '/billing') {
      window.location.assign('/billing');
    }
  },
});

export type { ApiFetchOptions };
export { selectCsrfToken };
export const { apiFetch, apiFetchJson, registerRefreshHandler } = transport;
